/**
 * Momentum alarms' words, figures and wire shapes: the three changes of
 * pace, their glyphs, the line every projection carries, and the
 * formatters every channel prints pace with (dashboard, email, Weekly
 * Sonar), so a figure reads the same wherever it shows. Shared with the SPA,
 * so nothing here may import server code.
 *
 * A raise's usual pace is the median of its daily amounts over the 7
 * complete days before the last 24 hours (src/pace/pace.model.ts).
 * Speeding up and Goal in sight take the accent; Slowing down is neutral.
 * None of them takes ▲, which already means "closes within 7 days", or the
 * urgency colours.
 */

import { currencySymbol } from '../feed/format';
import type { Deal } from '../feed/model';

export const PACE_KINDS = ['up', 'sight', 'down'] as const;
export type PaceKind = (typeof PACE_KINDS)[number];

export const PACE_GLYPHS: Record<PaceKind, string> = {
  up: '↗',
  sight: '◎',
  down: '↘',
};

/** What a projection rests on, ahead of a disclaimer that has its own end. */
export const PACE_CAVEAT =
  'Projections assume today’s pace continues. Pace often changes.';

/** On every block that shows a projection. */
export const PACE_DISCLAIMER = `${PACE_CAVEAT} Not investment advice.`;

/** Under the deadline of any email about a Reg CF raise. */
export const REG_CF_CANCEL =
  'Investors can cancel until 48 hours before the deadline, so totals can fall.';

/**
 * Each kind as a switch (deal_alert.lead_time, alert_rule.lead_times) and as
 * the event its alarm records (campaign_event.type): the same word.
 */
export const PACE_LEADS = {
  up: 'PACE_UP',
  sight: 'PACE_SIGHT',
  down: 'PACE_DOWN',
} as const satisfies Record<PaceKind, string>;
export type PaceLead = (typeof PACE_LEADS)[PaceKind];
export const PACE_LEAD_LIST: readonly PaceLead[] = PACE_KINDS.map(
  (k) => PACE_LEADS[k],
);

/** The kind a lead time or event type stands for; null for any other. */
export function paceKindOf(value: string): PaceKind | null {
  return PACE_KINDS.find((k) => PACE_LEADS[k] === value) ?? null;
}

export const PACE_TITLES: Record<PaceKind, string> = {
  up: 'Speeding up',
  sight: 'Goal in sight',
  down: 'Slowing down',
};

/** What each switch fires on, under its title. */
export const PACE_RULE_TEXT: Record<PaceKind, string> = {
  up: 'last 24h ≥ 2× usual pace',
  sight: 'target ≤ 48 h or max ≤ 72 h, at this pace',
  down: 'on course to miss target by close',
};

/** An email's type pill. */
export const PACE_PILLS: Record<PaceKind, string> = {
  up: '↗ SPEEDING UP',
  sight: '◎ GOAL IN SIGHT',
  down: '↘ SLOWING DOWN',
};

/** The master switch over a raise's three. */
export const PACE_SWITCH = 'Pace changes';

/** Two kinds true at once: the first of these is the one sent. */
export const PACE_PRIORITY: readonly PaceKind[] = ['sight', 'up', 'down'];

/** The accent pair, or the neutral one for Slowing down. */
export function paceTone(kind: PaceKind | null): 'accent' | 'neutral' {
  return kind === 'up' || kind === 'sight' ? 'accent' : 'neutral';
}

/** The thresholds and limits, in one place for the model, engine and copy. */
export const PACE_LIMITS = {
  /** Speeding up: the last 24 hours at least this many times usual. */
  upMultiple: 2,
  /** Goal in sight: the target within this many hours, at this pace… */
  sightTargetHours: 48,
  /** …or the maximum within this many, and before the close. */
  sightMaxHours: 72,
  /** Complete days of readings before pace is measured. */
  historyDays: 7,
  /** Days a chart shows, today's last 24 hours the last of them. */
  chartDays: 14,
  /** A kind fires again only once its condition has been clear this long. */
  rearmHours: 24,
  /** How long an alarm's chip shows on a followed raise's card. */
  chipHours: 72,
  /** Single pace emails a subscriber gets in a local day. */
  dailyCap: 3,
  /** Speeding up alarms due together that go as one bundle instead. */
  bundleMin: 3,
  /** Rows a bundle lists before "and n more". */
  bundleShown: 5,
} as const;

// ---- Formatting ----------------------------------------------------------------

/** "3.2×", or "3.2x" in plain text and SMS. One decimal. */
export function times(multiple: number, plain = false): string {
  return `${multiple.toFixed(1)}${plain ? 'x' : '×'}`;
}

/**
 * "$84k", "£9.4k", "€1.1k", "€330", "$1.24M": a day's amount in a compact
 * row, to a tenth of a thousand below ten thousand, so "usual £3.0k" sits
 * beside "+£6.4k".
 */
export function paceMoney(value: number, currency: string | null): string {
  const k = value / 1000;
  const figure =
    value >= 1_000_000
      ? `${Number((value / 1_000_000).toFixed(2))}M`
      : k >= 9.95
        ? `${Math.round(k)}k`
        : value >= 1000
          ? `${k.toFixed(1)}k`
          : String(Math.round(value));
  const symbol = currencySymbol(currency);
  return /^[A-Z]{3}$/.test(symbol) ? `${symbol} ${figure}` : symbol + figure;
}

/** "$84,000", "£22,300": a figure in a finding, in full. */
export function fullMoney(value: number, currency: string | null): string {
  const symbol = currencySymbol(currency);
  const figure = Math.round(value).toLocaleString('en-GB');
  return /^[A-Z]{3}$/.test(symbol) ? `${symbol} ${figure}` : symbol + figure;
}

/** A projected duration, in each form the surfaces print it. */
export interface PaceSpan {
  /** "~31 h", "~4 days". */
  short: string;
  /** "about 31 hours", "about 4 days". */
  words: string;
  /** A chip's: "~31h", "~4d". */
  chip: string;
}

/**
 * Under 48 hours in hours, 48 or more in whole days; never mixed units.
 * Rounded to the hour first, so 47.6 hours is "~2 days", not "~48 h".
 */
export function paceSpan(hours: number): PaceSpan {
  const h = Math.max(1, Math.round(hours));
  if (h < 48) {
    return {
      short: `~${h} h`,
      words: `about ${h} ${h === 1 ? 'hour' : 'hours'}`,
      chip: `~${h}h`,
    };
  }
  const d = Math.round(h / 24);
  return { short: `~${d} days`, words: `about ${d} days`, chip: `~${d}d` };
}

/** "Target", "Max". */
export const GOAL_WORDS = { target: 'Target', max: 'Max' } as const;
export type PaceGoal = keyof typeof GOAL_WORDS;

/** "a third", "a quarter", "half"; otherwise "0.4× its usual pace". */
export function slowShare(multiple: number, plain = false): string {
  const m = Math.round(multiple * 100) / 100;
  if (m === 0.25) return 'a quarter of its usual pace';
  const one = Math.round(multiple * 10) / 10;
  if (one === 0.3) return 'a third of its usual pace';
  if (one === 0.5) return 'half its usual pace';
  return `${times(multiple, plain)} its usual pace`;
}

// ---- What the API sends ----------------------------------------------------------

/**
 * Whether pace can be measured: MEASURED, or why not. HISTORY is fewer than
 * 7 complete days of readings; SPARSE a platform that publishes progress
 * less than once a day; ZERO a usual pace of nothing.
 */
export type PaceState = 'MEASURED' | 'HISTORY' | 'SPARSE' | 'ZERO';

/** A projection's goal and how far off it is, at this pace. */
export interface PaceEta {
  goal: PaceGoal;
  hours: number;
}

/** Why a measured raise has no ETA. */
export type NoEta = 'NO_GOAL' | 'AFTER_DEADLINE';

/** A measured raise's figures, as every surface formats them. */
export interface PaceFigures {
  /** Usual pace a day: the median of the 7 complete days before the last 24h. */
  usual: number;
  /** Raised in the last 24 hours. */
  last24: number;
  /** Investors in the last 24 hours, where the platform counts them. */
  last24Investors: number | null;
  /** last24 over usual, unrounded. */
  multiple: number;
  /** When it reaches a goal at this pace: the target, else the maximum. */
  eta: PaceEta | null;
  noEta: NoEta | null;
  /** Of the target, at this pace, by the close; null without both. */
  projectedPct: number | null;
  /** The kinds whose conditions hold now, in priority order. */
  active: PaceKind[];
}

/** An alarm that fired for the raise: its kind, and when. */
export interface PaceAlarmDto {
  kind: PaceKind;
  at: string;
}

/**
 * One raise's pace chip (GET /api/pace): its figures and the last alarm
 * within PACE_LIMITS.chipHours. Sent for followed raises with an alarm on
 * the Alerts plan, and for every measured raise on Pro.
 */
export interface PaceChipDto {
  multiple: number;
  eta: PaceEta | null;
  active: PaceKind[];
  alarm: PaceAlarmDto | null;
}

/** GET /api/pace: the viewer's chips, keyed by campaign id. */
export interface PaceChipsDto {
  /**
   * Whether Momentum alarms are on (MOMENTUM_ALARMS=on). Off, nothing about
   * pace shows anywhere: no chips, no Pace changes, no Speeding up chip.
   */
  enabled: boolean;
  /** Whether the viewer's plan sorts by pace ("Fastest filling"). */
  sortable: boolean;
  items: Record<string, PaceChipDto>;
}

/** One day of the chart and the Per day table. */
export interface PaceDayDto {
  /** "2026-10-27"; the last is today, the last 24 hours. */
  day: string;
  /** Null for a day before the first reading. */
  amount: number | null;
  investors: number | null;
}

/** A raise like this one, in "vs similar". */
export interface PaceSimilarRaiseDto {
  name: string;
  source: string;
  slug: string;
  /** "Republic · Reg CF · Robotics". */
  meta: string;
  chip: PaceChipDto;
}

/** "vs similar": live, measured raises in the same regime and sector. */
export interface PaceSimilarDto {
  /** "10 live Reg CF SaaS raises, $0.5–2M max". */
  label: string;
  count: number;
  /** Their median multiple; null with none. */
  median: number | null;
  top: PaceSimilarRaiseDto[];
}

/**
 * The deal page's Momentum panel (DealDetailDto.pace), shaped to the
 * viewer's plan: the daily series and "vs similar" only on Pro, null below.
 */
export interface DealPaceDto {
  state: PaceState;
  /** The platform's name, as the panel's provenance names it. */
  platform: string;
  currency: string | null;
  /** The latest reading; null with none. */
  checkedAt: string | null;
  /** Readings so far, and since when. */
  checks: number;
  since: string | null;
  /** HISTORY: complete days so far, 0–7, and the first day an alarm can go. */
  daysTracked: number;
  firstAlarmOn: string | null;
  /** SPARSE: how many times progress changed since `changesSince`. */
  changes: number;
  /** The first reading `changes` counts from (the last 16 days at most). */
  changesSince: string | null;
  /** MEASURED only. */
  figures: PaceFigures | null;
  /** The raise's last alarm within PACE_LIMITS.chipHours. */
  alarm: PaceAlarmDto | null;
  /** The last pace email this viewer got about the raise. */
  lastSent: PaceAlarmDto | null;
  /** Pro: the 14 days, oldest first. Null on every other plan. */
  days: PaceDayDto[] | null;
  /** Pro: raises like this one. Null on every other plan. */
  similar: PaceSimilarDto | null;
}

// ---- Chips ---------------------------------------------------------------------

export interface PaceChipView {
  /** The glyph's kind, or null for a plain multiple. */
  kind: PaceKind | null;
  text: string;
  tone: 'accent' | 'neutral';
}

/**
 * A chip's words. On a card, the watchlist and the Raises tab:
 * "↗ 3.2× usual", "◎ Target in ~31h", "↘ Slowing", or a plain "1.4×
 * usual" with no alarm. On the deal page's panel the multiple says "usual
 * pace". The alarm's kind decides the glyph; the figures are today's.
 */
export function paceChip(
  chip: Pick<PaceChipDto, 'multiple' | 'eta' | 'alarm'>,
  where: 'card' | 'panel' = 'card',
): PaceChipView {
  const kind = chip.alarm?.kind ?? null;
  const usual = where === 'panel' ? 'usual pace' : 'usual';
  if (kind === 'sight' && chip.eta) {
    const span = paceSpan(chip.eta.hours);
    return {
      kind,
      text: `◎ ${GOAL_WORDS[chip.eta.goal]} in ${where === 'panel' ? span.short : span.chip}`,
      tone: 'accent',
    };
  }
  if (kind === 'down' && where === 'card') {
    return { kind, text: '↘ Slowing', tone: 'neutral' };
  }
  if (kind === 'up' || kind === 'down') {
    return {
      kind,
      text: `${PACE_GLYPHS[kind]} ${times(chip.multiple)} ${usual}`,
      tone: paceTone(kind),
    };
  }
  return {
    kind: null,
    text: `${times(chip.multiple)} ${usual}`,
    tone: 'neutral',
  };
}

/** "↗ 2.4×", "◎ ~40 h", "↘ 0.4×", "1.1×": a bundle's or similar list's pill. */
export function pacePill(
  chip: Pick<PaceChipDto, 'multiple' | 'eta' | 'alarm'>,
  plain = false,
): PaceChipView {
  const kind = chip.alarm?.kind ?? null;
  if (kind === 'sight' && chip.eta) {
    return {
      kind,
      text: `◎ ${paceSpan(chip.eta.hours).short}`,
      tone: 'accent',
    };
  }
  const glyph = kind ? `${PACE_GLYPHS[kind]} ` : '';
  return {
    kind,
    text: `${glyph}${times(chip.multiple, plain)}`,
    tone: paceTone(kind),
  };
}

// ---- The pace line in other emails -------------------------------------------------

/** What a pace line reads off a raise. */
export interface PaceLineInput {
  state: PaceState;
  currency: string | null;
  figures: PaceFigures | null;
  /** HISTORY: complete days so far. */
  daysTracked: number;
  /** Stated goals, for the tail. */
  target: number | null;
  max: number | null;
}

/**
 * The line under a raise in Closing this week and inside the near-cap
 * block: "Pace $12k/day · 1.4× usual · at this pace 96% of target by
 * close", with the glyph in front only once an alarm's threshold is
 * crossed. Null where the platform does not publish progress often enough.
 * `towards` picks the tail: the target's share by close (Closing this
 * week), or when the maximum is reached (near cap).
 */
export function paceLine(
  input: PaceLineInput,
  towards: 'target' | 'max' = 'target',
): string | null {
  if (input.state === 'HISTORY') {
    const left = PACE_LIMITS.historyDays - input.daysTracked;
    return `Pace not measured yet · tracking for ${left} more ${left === 1 ? 'day' : 'days'}`;
  }
  const f = input.figures;
  if (input.state !== 'MEASURED' || !f) return null;
  const crossed = PACE_PRIORITY.find((k) => f.active.includes(k));
  const glyph = crossed ? `${PACE_GLYPHS[crossed]} ` : '';
  const head = `${glyph}Pace ${paceMoney(f.last24, input.currency)}/day · ${times(f.multiple)} usual`;
  return `${head} · ${paceTail(input, towards)}`;
}

function paceTail(input: PaceLineInput, towards: 'target' | 'max'): string {
  const f = input.figures!;
  if (towards === 'max') {
    if (f.eta?.goal === 'max') {
      return `at this pace max in ${paceSpan(f.eta.hours).words}`;
    }
    return f.noEta === 'AFTER_DEADLINE'
      ? 'at this pace the max comes after the deadline'
      : 'no maximum reported';
  }
  if (input.target !== null && f.projectedPct !== null) {
    return `at this pace ${Math.round(f.projectedPct)}% of target by close`;
  }
  if (input.max === null) return 'no maximum reported';
  return f.eta?.goal === 'max'
    ? `at this pace max in ${paceSpan(f.eta.hours).words}`
    : 'no target reported';
}

/**
 * One change of pace the alarm saw, as the Weekly Sonar's "Picked up pace
 * this week" reads it: the PACE_* events of the week.
 */
export interface PaceEvent {
  deal: Deal;
  kind: PaceKind;
  /** When the change was seen. */
  at: Date;
  /** Raised in the 24 hours to `at`, in the raise's currency. */
  day: number;
  /** Its usual pace: the median daily amount over the previous 7 days. */
  usual: number;
  /** The goal Goal in sight was about; null for the other two. */
  goal: { kind: 'target' | 'max'; amount: number } | null;
}
